import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress } from "@mui/material";
import { getAssessmentLock, unlockAssessmentForUser, getAssessmentResultsForUser } from "../../services/assessmentService";
import { logAdminAction } from "../../services/adminService";

export default function AdminAssessmentPanel({ user }) {
  const [lock, setLock] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      if (!user?.id) return;
      setLoading(true);
      try {
        const l = await getAssessmentLock(user.id);
        const r = await getAssessmentResultsForUser(user.id, 10);
        if (!active) return;
        setLock(l);
        setResults(r);
      } catch (e) {
        setError(e.message || "Failed to load assessment");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [user?.id]);

  const handleUnlock = async () => {
    setConfirm(false);
    try {
      await unlockAssessmentForUser(user.id, "ADMIN", "manual_admin_unlock");
      await logAdminAction("unlock_assessment", "ADMIN", user.id, {});
      const l = await getAssessmentLock(user.id);
      setLock(l);
    } catch (e) {
      setError(e.message || "Failed to unlock");
    }
  };

  return (
    <Card sx={{ borderTop: "6px solid #003366" }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 800, color: "#003366", mb: 2 }}>Assessment Control</Typography>
        {!user?.id ? (
          <Typography color="text.secondary">Select a user in Onboarding panel to manage assessment.</Typography>
        ) : loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
              <Chip label={`Status: ${lock?.locked ? "Locked" : "Unlocked"}`} color={lock?.locked ? "warning" : "success"} />
              <Chip label={`Attempts: ${results.length}`} />
              {lock?.locked && (
                <Button
                  variant="contained"
                  onClick={() => setConfirm(true)}
                  sx={{ backgroundColor: "#C1272D", "&:hover": { backgroundColor: "#A51F25" } }}
                >
                  Unlock Assessment
                </Button>
              )}
            </Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Recent Results</Typography>
            {results.length === 0 && (
              <Typography color="text.secondary">No submissions yet.</Typography>
            )}
            {results.map(r => (
              <Box key={r.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 1.5, mb: 1 }}>
                <Typography variant="body2">
                  Score: <strong>{r.score}%</strong> — {r.passed ? "Passed" : "Failed"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.createdAt?.toDate ? r.createdAt.toDate().toLocaleString() : "Unknown time"}
                </Typography>
              </Box>
            ))}
          </>
        )}

        <Dialog open={confirm} onClose={() => setConfirm(false)}>
          <DialogTitle>Confirm Unlock</DialogTitle>
          <DialogContent>
            <Typography>Allow this user to resubmit the onboarding assessment?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirm(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUnlock}>Unlock</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}
